import React, { useState, useEffect } from 'react';
import AddFilterButton from '../../shared/components/AddFilterButton';
import VolunteersSidebar from './VolunteersSidebar';

const sampleVolunteers = [
  {
    id: 1,
    name: 'Olivia Rhye',
    email: 'olivia@example.com',
    volunteerType: 'On-site',
    segments: ['Fan', 'Donor'],
    lastEvent: 'Mar 12 · Merch booth',
  },
  {
    id: 2,
    name: 'Phoenix Baker',
    email: 'phoenix@example.com',
    volunteerType: 'Remote',
    segments: ['Fan'],
    lastEvent: 'Feb 02 · Call campaign',
  },
  {
    id: 3,
    name: 'Lana Steiner',
    email: 'lana@example.com',
    volunteerType: 'Lead',
    segments: ['Fan', 'Volunteer'],
    lastEvent: 'Jan 18 · Check-in desk',
  },
];

function VolunteersPage({ team }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openRowMenuId, setOpenRowMenuId] = useState(null);

  useEffect(() => {
    if (!openRowMenuId) return;

    const handleDocumentClick = (event) => {
      const actionsCell = event.target.closest('.volunteers-actions-cell');
      if (!actionsCell) {
        setOpenRowMenuId(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [openRowMenuId]);

  const toggleSidebar = () => {
    setSidebarOpen((open) => !open);
  };

  const toggleRowMenu = (rowId) => {
    setOpenRowMenuId((current) => (current === rowId ? null : rowId));
  };

  const closeRowMenu = () => {
    setOpenRowMenuId(null);
  };

  return (
    <div className="volunteers-page" style={{ padding: 0 }}>
      <div className="row">
        {sidebarOpen && (
          <div className="col-md-3 mb-3">
            <VolunteersSidebar />
          </div>
        )}

        <div className={sidebarOpen ? 'col-md-9' : 'col-12'}>
          {/* Section header */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <button
                type="button"
                className="btn btn-light btn-sm me-3 d-inline-flex align-items-center justify-content-center"
                onClick={toggleSidebar}
                aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              >
                <span aria-hidden="true">
                  <svg
                    className="sprite-icon figma-icon secondary"
                    width="20"
                    height="20"
                    style={{ transform: sidebarOpen ? 'none' : 'scaleX(-1)' }}
                  >
                    <use xlinkHref="#arrows-left" />
                  </svg>
                </span>
              </button>
              <div className="flex-grow-1 ms-2">
                <h2 className="h4 mb-1">Volunteers</h2>
                <p className="text-muted mb-0">
                  {sampleVolunteers.length} volunteers
                </p>
              </div>

              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                >
                  <i className="fal fa-user-plus" aria-hidden="true" />
                  <span>Add volunteer</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table container */}
          <div className="card">
            <div className="card-body p-0">
              {/* Filters bar */}
              <div className="border-bottom px-4 py-3">
                <div className="d-flex justify-content-between align-items-end">
                  <div className="flex-grow-1 me-3">
                    <div className="team-search-wrapper">
                      <svg
                        className="sprite-icon figma-icon secondary team-search-icon"
                        width="16"
                        height="16"
                        aria-hidden="true"
                      >
                        <use xlinkHref="#search" />
                      </svg>
                      <input
                        type="search"
                        className="team-search-input"
                        placeholder="Search volunteers..."
                        aria-label="Search volunteers"
                      />
                    </div>
                  </div>

                  <div className="team-search-filters-right d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                    >
                      <svg
                        className="sprite-icon figma-icon secondary"
                        width="16"
                        height="16"
                        aria-hidden="true"
                      >
                        <use xlinkHref="#save" />
                      </svg>
                      <span>Save view</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                    >
                      <svg
                        className="sprite-icon figma-icon secondary"
                        width="16"
                        height="16"
                        aria-hidden="true"
                      >
                        <use xlinkHref="#settings" />
                      </svg>
                      <span>Table settings</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Add filter row */}
              <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                <AddFilterButton />
                <span className="text-muted small">
                  {sampleVolunteers.length} volunteers
                </span>
              </div>

              {/* Table */}
              <div className="table-responsive" style={{ overflow: 'visible' }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col" style={{ width: '1%' }}>
                        <input type="checkbox" aria-label="Select all volunteers" />
                      </th>
                      <th scope="col">Volunteer</th>
                      <th scope="col">Volunteer Type</th>
                      <th scope="col">Segment</th>
                      <th scope="col">Last Event</th>
                      <th scope="col" className="text-end" style={{ width: '1%' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleVolunteers.map((volunteer) => (
                      <tr key={volunteer.id}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${volunteer.name}`}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle bg-light me-3"
                              style={{ width: 32, height: 32 }}
                            />
                            <div>
                              <div className="fw-medium">{volunteer.name}</div>
                              <div className="text-muted small">
                                {volunteer.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{volunteer.volunteerType}</td>
                        <td>
                          {volunteer.segments.map((segment) => (
                            <span
                              key={segment}
                              className="badge bg-light text-dark me-1"
                            >
                              {segment}
                            </span>
                          ))}
                        </td>
                        <td className="text-nowrap">{volunteer.lastEvent}</td>
                        <td className="text-end position-relative volunteers-actions-cell">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-secondary text-decoration-none"
                            aria-label={`More actions for ${volunteer.name}`}
                            onClick={() => toggleRowMenu(volunteer.id)}
                          >
                            …
                          </button>

                          {openRowMenuId === volunteer.id && (
                            <div
                              className="dropdown-menu dropdown-menu-end show shadow-sm"
                              style={{ right: 0, left: 'auto' }}
                            >
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={closeRowMenu}
                              >
                                View details
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={closeRowMenu}
                              >
                                Edit volunteer
                              </button>
                              <div className="dropdown-divider" />
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={closeRowMenu}
                              >
                                Remove from team
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="border-top px-4 py-3 d-flex justify-content-center align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                    style={{ boxShadow: '0 1px 2px rgba(10,13,18,0.05)' }}
                  >
                    <i className="fal fa-arrow-left" aria-hidden="true" />
                    <span>Previous</span>
                  </button>

                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-light px-3"
                      aria-current="page"
                      style={{ boxShadow: '0 1px 2px rgba(10,13,18,0.05)' }}
                    >
                      1
                    </button>
                    <button type="button" className="btn btn-sm btn-link text-secondary">
                      2
                    </button>
                    <button type="button" className="btn btn-sm btn-link text-secondary">
                      3
                    </button>
                    <span className="text-muted px-1">…</span>
                    <button type="button" className="btn btn-sm btn-link text-secondary">
                      8
                    </button>
                    <button type="button" className="btn btn-sm btn-link text-secondary">
                      9
                    </button>
                    <button type="button" className="btn btn-sm btn-link text-secondary">
                      10
                    </button>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                    style={{ boxShadow: '0 1px 2px rgba(10,13,18,0.05)' }}
                  >
                    <span>Next</span>
                    <i className="fal fa-arrow-right" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VolunteersPage;
